import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { UserRole } from '@kfolks/types'
import { useDepartments, useCreateDepartment, useUpdateDepartment, useDeleteDepartment } from '@/hooks/useMaster'
import { roleAtLeast } from '@/auth'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
	type DragStartEvent,
	type DragOverEvent,
} from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'

interface MasterDepartment {
	departmentId: string
	code: string
	label: string
	isActive: boolean
	sortOrder: number
	parentId: string | null
	children?: MasterDepartment[]
}

interface EditingRow {
	id: string
	code: string
	label: string
	sortOrder: number
}

interface DropZoneProps {
	parentId: string | null
	position: number
	isOver: boolean
}

function DropZone({ parentId, position, isOver }: DropZoneProps) {
	const { setNodeRef } = useSortable({
		id: `dropzone-${parentId || 'root'}-${position}`,
		data: {
			type: 'dropzone',
			parentId,
			position,
		},
	})

	return (
		<div
			ref={setNodeRef}
			className={`h-2 transition-all ${isOver ? 'h-8 bg-blue-100 border-2 border-dashed border-blue-400 rounded' : ''}`}
		/>
	)
}

interface SortableDepartmentProps {
	dept: MasterDepartment
	level: number
	isEditing: boolean
	editingData: EditingRow | null
	onEdit: (row: EditingRow) => void
	onSaveEdit: () => void
	onCancelEdit: () => void
	onDelete: (id: string) => void
	onAddChild: (parentId: string) => void
	setEditingData: (data: EditingRow | null) => void
	updatePending: boolean
	deletePending: boolean
	addingParentId: string | null
	newRow: { code: string; label: string; sortOrder: number }
	setNewRow: (row: { code: string; label: string; sortOrder: number }) => void
	onSaveNew: () => void
	onCancelAdd: () => void
	createPending: boolean
	renderChildren: (children: MasterDepartment[], parentId: string, level: number) => React.ReactNode
	isDragging: boolean
	dropZoneOver: string | null
	isSuperAdmin: boolean
}

function SortableDepartment({
	dept,
	level,
	isEditing,
	editingData,
	onEdit,
	onSaveEdit,
	onCancelEdit,
	onDelete,
	onAddChild,
	setEditingData,
	updatePending,
	deletePending,
	addingParentId,
	newRow,
	setNewRow,
	onSaveNew,
	onCancelAdd,
	createPending,
	renderChildren,
	isDragging,
	dropZoneOver: _dropZoneOver,
	isSuperAdmin,
}: SortableDepartmentProps) {
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
		id: dept.departmentId,
		data: {
			type: 'department',
			department: dept,
		},
	})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.4 : 1,
	}

	const hasChildren = dept.children && dept.children.length > 0
	const isAdding = addingParentId === dept.departmentId

	return (
		<div className="mb-1" style={style}>
			<div
				ref={setNodeRef}
				className={`flex items-center gap-2 p-3 border rounded-lg transition-colors ${
					isDragging ? 'bg-gray-100 shadow-lg' : 'hover:bg-gray-50'
				}`}
				data-department-id={dept.departmentId}
			>
				<div
					{...attributes}
					{...listeners}
					className="cursor-grab active:cursor-grabbing flex-shrink-0"
					style={{ touchAction: 'none' }}
				>
					<GripVertical className="h-5 w-5 text-gray-400" />
				</div>

				{isEditing ? (
					<>
						<Input
							value={editingData?.code ?? ''}
							onChange={(e) => setEditingData(editingData ? { ...editingData, code: e.target.value } : null)}
							className="font-mono w-32"
						/>
						<Input
							value={editingData?.label ?? ''}
							onChange={(e) => setEditingData(editingData ? { ...editingData, label: e.target.value } : null)}
							className="flex-1"
						/>
						<Input
							type="number"
							value={editingData?.sortOrder ?? 0}
							onChange={(e) =>
								setEditingData(editingData ? { ...editingData, sortOrder: Number(e.target.value) } : null)
							}
							className="w-20"
						/>
						<Button onClick={onSaveEdit} size="sm" disabled={updatePending} className="h-7 px-2 text-xs">
							Save
						</Button>
						<Button onClick={onCancelEdit} size="sm" variant="outline" className="h-7 px-2 text-xs">
							Cancel
						</Button>
					</>
				) : (
					<>
						<span className="font-mono text-sm text-gray-600 w-32">{dept.code}</span>
						<span className="flex-1">{dept.label}</span>
						<span className="text-sm text-gray-500 w-20">#{dept.sortOrder}</span>
						{dept.isActive ? (
							<Badge className="w-20">Active</Badge>
						) : (
							<Badge variant="secondary" className="w-20">
								Inactive
							</Badge>
						)}
						{isSuperAdmin && (
							<div className="flex items-center gap-1">
								<Button
									onClick={() =>
										onEdit({
											id: dept.departmentId,
											code: dept.code,
											label: dept.label,
											sortOrder: dept.sortOrder,
										})
									}
									size="sm"
									variant="outline"
									className="h-7 px-2 text-xs"
								>
									Edit
								</Button>
								<Button
									onClick={() => onAddChild(dept.departmentId)}
									size="sm"
									variant="outline"
									className="h-7 px-2 text-xs"
								>
									Add Child
								</Button>
								<Button
									onClick={() => onDelete(dept.departmentId)}
									size="sm"
									variant="destructive"
									disabled={deletePending}
									className="h-7 px-2 text-xs"
								>
									Delete
								</Button>
							</div>
						)}
					</>
				)}
			</div>

			{isAdding && (
				<div className="flex items-center gap-2 p-3 border border-dashed rounded-lg bg-blue-50 mt-2 ml-8">
					<div className="w-5" />
					<Input
						value={newRow.code}
						onChange={(e) => setNewRow({ ...newRow, code: e.target.value })}
						placeholder="CODE"
						className="font-mono w-32"
					/>
					<Input
						value={newRow.label}
						onChange={(e) => setNewRow({ ...newRow, label: e.target.value })}
						placeholder="Display Label"
						className="flex-1"
					/>
					<Input
						type="number"
						value={newRow.sortOrder}
						onChange={(e) => setNewRow({ ...newRow, sortOrder: Number(e.target.value) })}
						className="w-20"
					/>
					<Button onClick={onSaveNew} size="sm" disabled={createPending} className="h-7 px-2 text-xs">
						Save
					</Button>
					<Button onClick={onCancelAdd} size="sm" variant="outline" className="h-7 px-2 text-xs">
						Cancel
					</Button>
				</div>
			)}

			{hasChildren && dept.children && (
				<div className="ml-8 mt-1">{renderChildren(dept.children, dept.departmentId, level + 1)}</div>
			)}
		</div>
	)
}

export function DepartmentsTree() {
	const { user } = useAuth()
	const isSuperAdmin = roleAtLeast(user?.role, UserRole.SUPER_ADMIN)
	const queryClient = useQueryClient()
	const { data: departments = [], isLoading, error } = useDepartments()
	const createMutation = useCreateDepartment()
	const updateMutation = useUpdateDepartment()
	const deleteMutation = useDeleteDepartment()

	const [editingId, setEditingId] = useState<string | null>(null)
	const [editingData, setEditingData] = useState<EditingRow | null>(null)
	const [addingParentId, setAddingParentId] = useState<string | null>(null)
	const [newRow, setNewRow] = useState({ code: '', label: '', sortOrder: 0 })
	const [activeId, setActiveId] = useState<string | null>(null)
	const [overId, setOverId] = useState<string | null>(null)

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 5,
			},
		}),
		useSensor(KeyboardSensor),
	)

	const handleEdit = (row: EditingRow) => {
		setEditingId(row.id)
		setEditingData(row)
	}

	const handleCancelEdit = () => {
		setEditingId(null)
		setEditingData(null)
	}

	const handleSaveEdit = async () => {
		if (!editingData || !editingId) return

		try {
			await updateMutation.mutateAsync({
				id: editingId,
				data: {
					code: editingData.code,
					label: editingData.label,
					sortOrder: editingData.sortOrder,
				},
			})
			setEditingId(null)
			setEditingData(null)
		} catch {}
	}

	const handleDelete = async (id: string) => {
		if (!confirm('Are you sure you want to delete this department?')) return

		try {
			await deleteMutation.mutateAsync(id)
		} catch {}
	}

	const handleAddChild = (parentId: string | null) => {
		setAddingParentId(parentId)
		setNewRow({ code: '', label: '', sortOrder: 0 })
	}

	const handleCancelAdd = () => {
		setAddingParentId(null)
		setNewRow({ code: '', label: '', sortOrder: 0 })
	}

	const handleSaveNew = async () => {
		if (!newRow.code || !newRow.label) {
			alert('Code and Label are required')
			return
		}

		try {
			await createMutation.mutateAsync({
				code: newRow.code,
				label: newRow.label,
				parentId: addingParentId || undefined,
				sortOrder: newRow.sortOrder,
			})
			setAddingParentId(null)
			setNewRow({ code: '', label: '', sortOrder: 0 })
		} catch {}
	}

	// Flatten tree to get all IDs (departments + dropzones)
	const flattenTree = (tree: MasterDepartment[]): string[] => {
		const ids: string[] = []

		const traverse = (nodes: MasterDepartment[], parentId: string | null) => {
			// Add dropzone before first item
			ids.push(`dropzone-${parentId || 'root'}-0`)

			nodes.forEach((node, index) => {
				ids.push(node.departmentId)
				// Add dropzone after each item
				ids.push(`dropzone-${parentId || 'root'}-${index + 1}`)

				if (node.children && node.children.length > 0) {
					traverse(node.children, node.departmentId)
				}
			})
		}

		traverse(tree, null)
		return ids
	}

	// Find department by ID in the tree
	const findDepartmentById = (tree: MasterDepartment[], id: string): MasterDepartment | null => {
		for (const dept of tree) {
			if (dept.departmentId === id) return dept
			if (dept.children) {
				const found = findDepartmentById(dept.children, id)
				if (found) return found
			}
		}
		return null
	}

	// Check if targetId is a descendant of sourceId
	const isDescendant = (tree: MasterDepartment[], sourceId: string, targetId: string): boolean => {
		const source = findDepartmentById(tree, sourceId)
		if (!source || !source.children) return false

		for (const child of source.children) {
			if (child.departmentId === targetId) return true
			if (isDescendant(tree, child.departmentId, targetId)) return true
		}
		return false
	}

	const handleDragStart = (event: DragStartEvent) => {
		setActiveId(event.active.id as string)
	}

	const handleDragOver = (event: DragOverEvent) => {
		setOverId(event.over?.id as string | null)
	}

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event
		setActiveId(null)
		setOverId(null)

		if (!over || active.id === over.id) return

		const draggedId = active.id as string
		const overId = over.id as string

		// Check if dropping on a dropzone
		if (overId.startsWith('dropzone-')) {
			const parts = overId.split('-')
			const targetParentId = parts[1] === 'root' ? null : parts[1]
			const position = parseInt(parts[2], 10)

			// Prevent dropping into own descendants
			if (targetParentId && isDescendant(departments as MasterDepartment[], draggedId, targetParentId)) {
				alert('Cannot move a department into its own child')
				return
			}

			try {
				await updateMutation.mutateAsync({
					id: draggedId,
					data: {
						parentId: targetParentId || undefined,
						sortOrder: position,
					},
				})

				await queryClient.invalidateQueries({ queryKey: ['departments'] })
			} catch {
				await queryClient.invalidateQueries({ queryKey: ['departments'] })
			}
		}
	}

	const renderChildren = (children: MasterDepartment[], parentId: string | null, level: number) => {
		const sorted = [...children].sort((a, b) => a.sortOrder - b.sortOrder)

		return (
			<>
				{/* Dropzone before first child */}
				<DropZone parentId={parentId} position={0} isOver={overId === `dropzone-${parentId || 'root'}-0`} />

				{sorted.map((child, index) => (
					<div key={child.departmentId}>
						<SortableDepartment
							dept={child}
							level={level}
							isEditing={editingId === child.departmentId}
							editingData={editingData}
							onEdit={handleEdit}
							onSaveEdit={() => void handleSaveEdit()}
							onCancelEdit={handleCancelEdit}
							onDelete={(id: string) => void handleDelete(id)}
							onAddChild={handleAddChild}
							setEditingData={setEditingData}
							updatePending={updateMutation.isPending}
							deletePending={deleteMutation.isPending}
							addingParentId={addingParentId}
							newRow={newRow}
							setNewRow={setNewRow}
							onSaveNew={() => void handleSaveNew()}
							onCancelAdd={handleCancelAdd}
							createPending={createMutation.isPending}
							renderChildren={renderChildren}
							isDragging={activeId === child.departmentId}
							dropZoneOver={overId}
							isSuperAdmin={isSuperAdmin}
						/>

						{/* Dropzone after each child */}
						<DropZone
							parentId={parentId}
							position={index + 1}
							isOver={overId === `dropzone-${parentId || 'root'}-${index + 1}`}
						/>
					</div>
				))}
			</>
		)
	}

	if (isLoading) {
		return <div className="text-center py-8">Loading departments...</div>
	}

	if (error) {
		return <div className="text-center py-8 text-red-600">Error loading departments: {error.message}</div>
	}

	const tree = (departments as MasterDepartment[]) || []
	const isAddingRoot = addingParentId === null
	const allIds = flattenTree(tree)

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<p className="text-sm text-gray-600">
					{tree.length} root department{tree.length !== 1 ? 's' : ''}
				</p>
				{isSuperAdmin && (
					<Button onClick={() => handleAddChild(null)} disabled={isAddingRoot} size="sm">
						Add Root Department
					</Button>
				)}
			</div>

			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnd={(event) => void handleDragEnd(event)}
			>
				<SortableContext items={allIds} strategy={verticalListSortingStrategy}>
					<div className="space-y-2">
						{isAddingRoot && (
							<div className="flex items-center gap-2 p-3 border border-dashed rounded-lg bg-blue-50">
								<div className="w-5" />
								<Input
									value={newRow.code}
									onChange={(e) => setNewRow({ ...newRow, code: e.target.value })}
									placeholder="CODE"
									className="font-mono w-32"
								/>
								<Input
									value={newRow.label}
									onChange={(e) => setNewRow({ ...newRow, label: e.target.value })}
									placeholder="Display Label"
									className="flex-1"
								/>
								<Input
									type="number"
									value={newRow.sortOrder}
									onChange={(e) => setNewRow({ ...newRow, sortOrder: Number(e.target.value) })}
									className="w-20"
								/>
								<Button
									onClick={() => void handleSaveNew()}
									size="sm"
									disabled={createMutation.isPending}
									className="h-7 px-2 text-xs"
								>
									Save
								</Button>
								<Button onClick={handleCancelAdd} size="sm" variant="outline" className="h-7 px-2 text-xs">
									Cancel
								</Button>
							</div>
						)}

						{renderChildren(tree, null, 0)}

						{!isAddingRoot && tree.length === 0 && (
							<div className="text-center py-8 text-gray-500 border rounded-lg">
								No departments found. Click "Add Root Department" to create one.
							</div>
						)}
					</div>
				</SortableContext>
			</DndContext>
		</div>
	)
}
